/**
 * Download rows as a CSV file that opens straight in Excel with Swedish settings:
 * semicolon separated, decimal comma, UTF-8 with BOM so å/ä/ö survive.
 */
export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined;
}

const cell = (value: unknown) => {
  if (value === null || value === undefined) return '';
  let text = typeof value === 'number' ? String(value).replace('.', ',') : String(value);
  if (text.startsWith('0001-')) text = '';
  // Stop Excel from treating text as a formula
  if (/^[=+\-@]/.test(text) && typeof value !== 'number') text = `'${text}`;
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function exportCsv<T>(fileName: string, columns: CsvColumn<T>[], rows: T[]) {
  const lines = [
    columns.map((c) => cell(c.header)).join(';'),
    ...rows.map((row) => columns.map((c) => cell(c.value(row))).join(';')),
  ];
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, fileName.endsWith('.csv') ? fileName : `${fileName}.csv`);
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The file name from a Content-Disposition header, or the fallback */
export function fileNameFrom(contentDisposition: string | undefined, fallback: string) {
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition || '');
  return match ? match[1] : fallback;
}
